import type { EmptyObject } from 'type-fest'
import { ImportIntegrationProjectNameSource } from '../enums.js'

export type SpectrumCostDataSource = {
  table: string
  field: string
}

export const SPECTRUM_DEFAULT_PROJECTED_COST_SOURCE: SpectrumCostDataSource = {
  table: 'JC_PROJECTED_ESTIMATED_BY_JOB_MC',
  field: 'Estimated_Cost',
}

/** Map from Spectrum table name to available field names for projected cost */
export const SPECTRUM_PROJECTED_COST_TABLES: Record<string, string[]> = {
  JC_PROJECTED_ESTIMATED_BY_JOB_MC: ['Estimated_Cost', 'Projected_Cost'],
  JC_JOB_MASTER_MC: ['Entered_Projected_Cost'],
}

export type CompanyIntegrationMetadataSpectrum = {
  // Map from user ID to batch code
  batches?: { [userId: string]: string }
  companyCode?: string
  // Override for projected cost data source (defaults to JC_PROJECTED_ESTIMATED_BY_JOB_MC / Estimated_Cost)
  projectedCostSource?: SpectrumCostDataSource
  // When true, the bulk vendor invoice import starts from the oldest vendor contract commitment
  // date for the company, minus `BULK_VENDOR_INVOICE_IMPORT_COMMITMENT_BUFFER_MONTHS`. Falls back
  // to the standard `BULK_VENDOR_INVOICE_IMPORT_PERIOD_MONTHS` lookback when the company has no
  // commitments.
  usesOldestCommitmentDateForBulkApImport?: boolean
}

// This type allows a user to set a company wide default GL accounts for income, retainage, and A/R
export type CompanyIntegrationMetadataFoundation = {
  // We call this the "Retainage" account in the UI instead of "Retention"
  retentionLedgerAccount?: {
    agaveSourceId: string
    name: string
  }
  arLedgerAccount?: {
    agaveSourceId: string
    name: string
  }
  ledgerAccount?: {
    agaveAccountId: string
    name?: string
    code?: string
  }
  incomeTypeId?: string | null
}

// This type allows a user to set a company wide default income account
// for a Sage 100 integration
export type CompanyIntegrationMetadataSage100Contractor = {
  incomeAccount?: {
    hh2AccountId: string
    hh2AccountName?: string
    hh2AccountCode?: string
  }
}

// This type allows a user to set a company wide default income account
// for a Sage 100 Agave integration
export type CompanyIntegrationMetadataSage100ContractorAgave = {
  incomeAccount?: {
    agaveAccountId: string
    agaveAccountName?: string
    agaveAccountCode?: string

    subsidiaryAccountRef?: string
    subsidiaryAccountName?: string
    subsidiaryAccountCode?: string
  }
}

// This type allows a user to set a company wide default income account
// for a Sage 100 Peyote integration
export type CompanyIntegrationMetadataSage100ContractorPeyote = {
  incomeAccount?: {
    peyoteAccountId: string
    peyoteAccountName?: string
    peyoteAccountCode?: string

    subsidiaryAccountRef?: string
    subsidiaryAccountName?: string
    subsidiaryAccountCode?: string
  }
}

export enum CompanyIntegrationMetadataVistaFallbackAddressField {
  ShipAddress = 'ShipAddress',
  GcAddress = 'GcAddress',
}

export type CompanyIntegrationMetadataVista = {
  // If the customer prefers entering invoice codes manually, default to manual entry when syncing
  useManualInvoiceCode?: boolean
  // If the customer prefers entering batch numbers manually, default to manual entry when syncing
  useManualBatchNumber?: boolean
  // If the contract line items have valid (contiguous) billing groups, we will import them as headings in the SOV
  groupLineItemsByBillGroup?: boolean
  // Fallback address field to use if the mail address is not available
  fallbackAddressField?: CompanyIntegrationMetadataVistaFallbackAddressField
  writeToJbBilling?: boolean
}

export type CompanyIntegrationMetadataVistaPeyote = {
  // If the customer prefers entering invoice codes manually, default to manual entry when syncing
  useManualInvoiceCode?: boolean
  // If the customer prefers entering batch numbers manually, default to manual entry when syncing
  useManualBatchNumber?: boolean
  // If the contract line items have valid (contiguous) billing groups, we will import them as headings in the SOV
  groupLineItemsByBillGroup?: boolean
  // Fallback address field to use if the mail address is not available
  fallbackAddressField?: CompanyIntegrationMetadataVistaFallbackAddressField
  writeToJbBilling?: boolean
}

export const SAGE_INTACCT_INVOICE_TYPE_REGULAR = 'Regular'
export const SAGE_INTACCT_INVOICE_TYPE_PROJECT_CONTRACT = 'Project Contract'

// Settings shared by the Agave-backed (`SAGE_INTACCT`) and peyote-backed
// (`SAGE_INTACCT_PEYOTE`) Sage Intacct integrations. The two concrete metadata types below
// differ only in how the cost ledger account range identifies accounts.
// This type allows an admin to set the billing type that we will write invoices
// to for a given Sage Intacct instance. So far, we've seen AIA Billing and
// Project Contract billing types
type CompanyIntegrationMetadataSageIntacctCommon = {
  invoiceType?: string
  retentionInvoiceType?: string
  hasAutoNumberingEnabled?: boolean

  // Multi-entity companies: the top-level entity (a Sage Intacct LOCATION with no
  // parent) this integration is scoped to. Chosen at setup; every gateway session
  // logs into this entity, mirroring how the Agave connection is entity-scoped.
  entityLocationId?: string
  // If true, allow importing projects with no contract
  includeProjectsWithoutContract?: boolean
  // If true, use internal project number when syncing pay apps. If false or undefined, use GC project number.
  useInternalProjectNumberForPayApps?: boolean
  // If true, import the project's CUSTOMERID as the contract's vendor number. Only correct for
  // customers whose Intacct customer IDs are the vendor numbers their GCs assigned them.
  importCustomerIdAsVendorNumber?: boolean

  // Sage Intacct can record the same transaction in multiple books (e.g. ACCRUAL, CCSTACCRUAL).
  // When set, ledger entries whose BOOKID matches any of these IDs are excluded from cost
  // calculations to avoid double-counting.
  excludedCostBookIds?: string[]
}

// When calculating costs, we'll filter ledger entries to only include those between the start and
// end accounts. This is necessary because ledger entries have a lot of duplicates, and customers
// also track costs in specific ledger accounts. Both *must* be provided for reading costs.
type SageIntacctCostLedgerAccountAgave = {
  agaveLedgerAccountId: string
  sageLedgerAccountName: string
  sageLedgerAccountCode: string
}
type SageIntacctCostLedgerAccountPeyote = {
  peyoteLedgerAccountId: string
  sageLedgerAccountName: string
  sageLedgerAccountCode: string
}

// Agave-backed Sage Intacct: the cost ledger range is keyed by Agave ledger account UUID.
export type CompanyIntegrationMetadataSageIntacct = CompanyIntegrationMetadataSageIntacctCommon & {
  costLedgerAccountRangeStart?: SageIntacctCostLedgerAccountAgave
  costLedgerAccountRangeEnd?: SageIntacctCostLedgerAccountAgave
}

// Peyote-backed Sage Intacct: the cost ledger range is keyed by peyote ID (`pey_…`).
export type CompanyIntegrationMetadataSageIntacctPeyote =
  CompanyIntegrationMetadataSageIntacctCommon & {
    costLedgerAccountRangeStart?: SageIntacctCostLedgerAccountPeyote
    costLedgerAccountRangeEnd?: SageIntacctCostLedgerAccountPeyote
  }

export type CompanyIntegrationMetadataCmic = {
  companyCode: string
  collectorCode: string
}

export type CompanyIntegrationMetadataQuickbooksOnline = {
  companyCode: string
  /**
   * If true, invoices synced to QuickBooks Online will be net of retention (retention deducted from invoice total).
   * If false (or absent, on older integrations), invoices will sync the gross amount, including retention.
   */
  deductRetentionFromInvoice?: boolean
  /**
   * If true, a date range filter is shown when importing projects from QuickBooks Online, allowing
   * users to narrow results by project creation date. Defaults to false (date filter is hidden).
   */
  showProjectImportDateFilter?: boolean
}

export type CompanyIntegrationMetadataNetSuite = {
  subsidiary: string
  arAccount: string
  progressItem: string
  retentionItem: string
}

/**
 * Exporting invoices for Quickbooks requires referencing a Quickbooks "Item" for every line on the
 * invoice, where each Item has an associate income account (i.e. Account entry with type INC).
 * Based on conversations with customers, it seems that customers most commonly use a single item
 * type for all progress items on their invoices, and another item type for all retention items.
 * Rather than have to set the item and account each time an invoice is exported on a new project,
 * customers will set these mappings once in their company settings, and they will be used on all
 * invoices exported for that company. These settings can also be override on a per-project basis,
 * by defining the corresponding value in the `Integration.metadata`.
 */
export type CompanyIntegrationMetadataQuickbooks = {
  /**
   * This is the AR account used for all progress pay apps exported to QuickBooks
   */
  accountsReceivableAccount: string
  /**
   * This is the AR account used for all retention pay apps exported to QuickBooks
   */
  retentionAccountsReceivableAccount?: string
  /**
   * This is the invoice item that will be used for all progress line items on an invoice export
   * IIF. It corresponds to the name of an !INVITEM entry in each invoice IIF file, which will
   * initially create and then reference an Item in Quickbooks.
   */
  progressItemName: string
  /**
   * This is the invoice account corresponding to the `progressItemName` above. It will be used on
   * the same !INVITEM entry in each invoice IIF file.
   */
  progressItemIncomeAccount: string
  /**
   * This is the invoice item that will be used for all retention line items on an invoice export
   * IIF. It corresponds to the name of an !INVITEM entry in each invoice IIF file, which will
   * initially create and then reference an Item in Quickbooks.
   */
  retentionItemName: string
  /**
   * This is the invoice account corresponding to the `retentionItemName` above. It will be used on
   * the same !INVITEM entry in each invoice IIF file.
   */
  retentionItemIncomeAccount: string
}

type CompanyIntegrationMetadataDefault = EmptyObject

export type CompanyIntegrationMetadata =
  | CompanyIntegrationMetadataSpectrum
  | CompanyIntegrationMetadataQuickbooks
  | CompanyIntegrationMetadataDefault
  | CompanyIntegrationMetadataSageIntacct
  | CompanyIntegrationMetadataSageIntacctPeyote
  | CompanyIntegrationMetadataFoundation
  | CompanyIntegrationMetadataVista
  | CompanyIntegrationMetadataVistaPeyote
  | CompanyIntegrationMetadataCmic
  | CompanyIntegrationMetadataQuickbooksOnline

// Interface used for storing past invoice codes for an hh2 integration, along with the latest hh2
// version number found in the last invoice response
export type Hh2CachedInvoiceCodes = {
  invoiceCodes: string[]
  version: number
}

// Interface used for storing past invoice codes for an Agave integration, along with the latest
// date found in the last invoice response
export type AgaveCachedInvoiceCodes = {
  invoiceCodes: string[]
  lastDate: string
}

export const DEFAULT_IMPORT_INTEGRATION_PROJECT_NAME_SOURCE =
  ImportIntegrationProjectNameSource.JOB_NAME

export function getSitelineProjectNameFromIntegrationProject({
  integrationProjectName,
  integrationContractName,
  importProjectNameSource,
}: {
  integrationProjectName: string
  integrationContractName: string | null | undefined
  importProjectNameSource: ImportIntegrationProjectNameSource
}): string {
  switch (importProjectNameSource) {
    case ImportIntegrationProjectNameSource.JOB_NAME:
      return integrationProjectName
    case ImportIntegrationProjectNameSource.CONTRACT_NAME:
      // We still fall back to the job name if there's no contract name, e.g. if the integration
      // doesn't always have a contract per job or the contract name isn't filled because it's the
      // same as the job name
      return integrationContractName || integrationProjectName
  }
}
